import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle, XCircle, Trash2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

interface ServiceProvider {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  company_name: string;
  is_active: boolean;
  is_verified: boolean;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  created_at: string;
}

const ServiceProvidersManagement = () => {
  const { toast } = useToast();
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [pendingProviders, setPendingProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      
      // Fetch providers ready for subscription activation 
      // (email verified => is_active=true) but not yet subscribed (is_verified=false)
      const { data: pendingData } = await supabase
        .from('service_providers')
        .select('*')
        .eq('is_active', true)
        .eq('is_verified', false)
        .order('created_at', { ascending: false });

      setPendingProviders(pendingData || []);

      // Fetch active providers (verified subscription)
      const { data: activeData } = await supabase
        .from('service_providers')
        .select('*')
        .eq('is_active', true)
        .eq('is_verified', true)
        .order('subscription_end_date', { ascending: true });

      setProviders(activeData || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const activateProvider = async (providerId: string) => {
    try {
      const { error } = await supabase.rpc('activate_service_provider', {
        p_provider_id: providerId
      });

      if (error) throw error;

      toast({
        title: "تم تفعيل صاحب الخدمة",
        description: "تم تفعيل الحساب لمدة شهر واحد",
      });

      fetchProviders();
    } catch (error: any) {
      toast({
        title: "خطأ",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const deactivateProvider = async (providerId: string) => {
    try {
      const { error } = await supabase
        .from('service_providers')
        .update({ 
          is_active: false,
          is_verified: false 
        })
        .eq('id', providerId);

      if (error) throw error;

      toast({
        title: "تم إيقاف صاحب الخدمة",
        description: "تم إيقاف الحساب بنجاح",
      });

      fetchProviders();
    } catch (error: any) {
      toast({
        title: "خطأ",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const deleteProvider = async (providerId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا الحساب؟ سيتم حذف جميع الخدمات والحجوزات المرتبطة به.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('service_providers')
        .delete()
        .eq('id', providerId);

      if (error) throw error;

      toast({
        title: "تم الحذف",
        description: "تم حذف الحساب بنجاح",
      });

      fetchProviders();
    } catch (error: any) {
      toast({
        title: "خطأ",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const getDaysRemaining = (endDate: string | null) => {
    if (!endDate) return null;
    const diff = new Date(endDate).getTime() - new Date().getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getStatusBadge = (daysRemaining: number | null) => {
    if (daysRemaining === null) return null;
    if (daysRemaining < 0) {
      return <Badge variant="destructive">منتهي</Badge>;
    } else if (daysRemaining <= 5) {
      return <Badge variant="destructive">ينتهي قريباً ({daysRemaining} يوم)</Badge>;
    } else {
      return <Badge variant="default">نشط ({daysRemaining} يوم)</Badge>;
    }
  };

  if (loading) {
    return <div className="text-center py-8">جاري التحميل...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>إدارة أصحاب الخدمات</CardTitle>
          <CardDescription>
            عرض وإدارة أصحاب الخدمات وطلبات تفعيل الاشتراك (بعد تأكيد البريد)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="pending" dir="rtl">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="pending">
                طلبات تفعيل الاشتراك ({pendingProviders.length})
              </TabsTrigger>
              <TabsTrigger value="active">
                الاشتراكات النشطة ({providers.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="mt-6">
              {pendingProviders.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  لا توجد طلبات تفعيل اشتراك جديدة
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-right">الاسم</TableHead>
                      <TableHead className="text-right">اسم الشركة</TableHead>
                      <TableHead className="text-right">البريد الإلكتروني</TableHead>
                      <TableHead className="text-right">الهاتف</TableHead>
                      <TableHead className="text-right">تاريخ التسجيل</TableHead>
                      <TableHead className="text-right">الإجراءات</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingProviders.map((provider) => (
                      <TableRow key={provider.id}>
                        <TableCell className="font-medium">{provider.full_name}</TableCell>
                        <TableCell>{provider.company_name}</TableCell>
                        <TableCell>{provider.email}</TableCell>
                        <TableCell>{provider.phone}</TableCell>
                        <TableCell>
                          {format(new Date(provider.created_at), 'dd/MM/yyyy', { locale: ar })}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => activateProvider(provider.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="ml-2 h-4 w-4" />
                              تفعيل
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteProvider(provider.id)}
                            >
                              <Trash2 className="ml-2 h-4 w-4" />
                              حذف
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="active" className="mt-6">
              {providers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  لا يوجد أصحاب خدمات نشطون حالياً
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-right">الاسم</TableHead>
                      <TableHead className="text-right">اسم الشركة</TableHead>
                      <TableHead className="text-right">البريد الإلكتروني</TableHead>
                      <TableHead className="text-right">الحالة</TableHead>
                      <TableHead className="text-right">تاريخ الانتهاء</TableHead>
                      <TableHead className="text-right">الإجراءات</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {providers.map((provider) => {
                      const daysRemaining = getDaysRemaining(provider.subscription_end_date);
                      return (
                        <TableRow key={provider.id}>
                          <TableCell className="font-medium">{provider.full_name}</TableCell>
                          <TableCell>{provider.company_name}</TableCell>
                          <TableCell>{provider.email}</TableCell>
                          <TableCell>{getStatusBadge(daysRemaining)}</TableCell>
                          <TableCell>
                            {provider.subscription_end_date
                              ? format(new Date(provider.subscription_end_date), 'dd/MM/yyyy', { locale: ar })
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {daysRemaining !== null && daysRemaining < 0 ? (
                                <Button
                                  size="sm"
                                  onClick={() => activateProvider(provider.id)}
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  <CheckCircle className="ml-2 h-4 w-4" />
                                  تجديد
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => deactivateProvider(provider.id)}
                                >
                                  <XCircle className="ml-2 h-4 w-4" />
                                  إيقاف
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteProvider(provider.id)}
                              >
                                <Trash2 className="ml-2 h-4 w-4" />
                                حذف
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceProvidersManagement;
